import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  CalendarDays,
  CheckCircle2,
  Eye,
  GraduationCap,
  Pencil,
  Plus,
  UserRound,
  Users
} from 'lucide-react';
import { db, functions } from '../../core/firebase/firebase';

const EMPTY_TEACHER = {
  fullname: '',
  username: '',
  phone: '',
  personalEmail: '',
  temporaryPassword: ''
};

const EMPTY_ASSIGNMENT = {
  teacherId: '',
  classId: '',
  subjectId: ''
};

const EMPTY_TIMETABLE = {
  teacherId: '',
  classId: '',
  subjectId: '',
  day: 'Lundi',
  startTime: '',
  endTime: '',
  room: ''
};

function teacherName(teacher) {
  return teacher?.fullname || teacher?.fullName || teacher?.username || teacher?.email || 'Enseignant';
}

function schoolClassName(schoolClass) {
  return schoolClass?.name || schoolClass?.className || schoolClass?.id || 'Classe';
}

function subjectName(subject) {
  return subject?.name || subject?.subjectName || subject?.id || 'Matière';
}

export default function TeachersPage({ mode = 'list' }) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState('');

  const [teacherForm, setTeacherForm] = useState(EMPTY_TEACHER);
  const [teacherSubmitting, setTeacherSubmitting] = useState(false);
  const [teacherError, setTeacherError] = useState('');
  const [teacherSuccess, setTeacherSuccess] = useState('');

  const [editingTeacherId, setEditingTeacherId] = useState('');
  const [editForm, setEditForm] = useState(EMPTY_TEACHER);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const [assignment, setAssignment] = useState(EMPTY_ASSIGNMENT);
  const [assignmentSubmitting, setAssignmentSubmitting] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentSuccess, setAssignmentSuccess] = useState('');

  const [timetable, setTimetable] = useState(EMPTY_TIMETABLE);
  const [timetableSubmitting, setTimetableSubmitting] = useState(false);
  const [timetableError, setTimetableError] = useState('');
  const [timetableSuccess, setTimetableSuccess] = useState('');

  async function loadData() {
    setLoading(true);
    setDataError('');

    try {
      const [usersSnapshot, classesSnapshot, subjectsSnapshot, assignmentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'subjects')),
        getDocs(collection(db, 'teacherAssignments'))
      ]);

      const loadedTeachers = usersSnapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((user) => ['teacher', 'enseignant'].includes(String(user.accountType || user.role || '').toLowerCase()))
        .filter((user) => user.status !== 'disabled')
        .sort((a, b) => teacherName(a).localeCompare(teacherName(b), 'fr'));

      setTeachers(loadedTeachers);
      setClasses(
        classesSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status !== 'inactive')
      );
      setSubjects(
        subjectsSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status !== 'inactive')
      );
      setTeacherAssignments(
        assignmentsSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status === 'active')
      );
    } catch (error) {
      console.error('Chargement enseignants :', error);
      setDataError('Impossible de charger les informations des enseignants.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setTeacherError('');
    setTeacherSuccess('');
    setEditError('');
    setEditSuccess('');
    setAssignmentError('');
    setAssignmentSuccess('');
    setTimetableError('');
    setTimetableSuccess('');
    setEditingTeacherId('');
  }, [mode]);

  const teachersWithAssignments = useMemo(
    () =>
      teachers.map((teacher) => ({
        ...teacher,
        pedagogicalAssignments: teacherAssignments
          .filter((item) => item.teacherId === teacher.id && item.status === 'active')
          .map((item) => ({
            id: item.id,
            className: schoolClassName(classes.find((candidate) => candidate.id === item.classId) || { id: item.classId }),
            subjectName: subjectName(subjects.find((candidate) => candidate.id === item.subjectId) || { id: item.subjectId })
          }))
      })),
    [teachers, teacherAssignments, classes, subjects]
  );

  const assignmentSubjects = useMemo(() => {
    if (!assignment.classId) return [];
    return subjects.filter((item) => !Array.isArray(item.classIds) || item.classIds.length === 0 || item.classIds.includes(assignment.classId));
  }, [subjects, assignment.classId]);

  const timetableTeacherAssignments = useMemo(
    () => teacherAssignments.filter((item) => item.teacherId === timetable.teacherId && item.status === 'active'),
    [teacherAssignments, timetable.teacherId]
  );

  const timetableClasses = useMemo(() => {
    const ids = [...new Set(timetableTeacherAssignments.map((item) => item.classId))];
    return ids.map((id) => classes.find((item) => item.id === id)).filter(Boolean);
  }, [timetableTeacherAssignments, classes]);

  const timetableSubjects = useMemo(() => {
    if (!timetable.classId) return [];
    const ids = [
      ...new Set(
        timetableTeacherAssignments
          .filter((item) => item.classId === timetable.classId)
          .map((item) => item.subjectId)
      )
    ];
    return ids.map((id) => subjects.find((item) => item.id === id)).filter(Boolean);
  }, [timetableTeacherAssignments, timetable.classId, subjects]);

  function updateTeacherField(event) {
    const { name, value } = event.target;
    setTeacherForm((current) => ({ ...current, [name]: value }));
  }

  async function submitTeacher(event) {
    event.preventDefault();
    setTeacherSubmitting(true);
    setTeacherError('');
    setTeacherSuccess('');

    try {
      const createTeacherAccount = httpsCallable(functions, 'createTeacherAccount');
      const result = await createTeacherAccount({
        ...teacherForm,
        schoolYear: '2026-2027'
      });

      setTeacherSuccess(`Le compte de ${result.data.fullname} a été créé avec succès.`);
      setTeacherForm(EMPTY_TEACHER);
      await loadData();
    } catch (error) {
      console.error('Création enseignant :', error);
      if (error.code === 'functions/already-exists') {
        setTeacherError('Cet identifiant enseignant existe déjà.');
      } else if (error.code === 'functions/permission-denied') {
        setTeacherError('Cette action est réservée à la gérance.');
      } else {
        setTeacherError(error.message || 'Impossible de créer le compte enseignant.');
      }
    } finally {
      setTeacherSubmitting(false);
    }
  }

  function startEditing(teacher) {
    setEditingTeacherId(teacher.id);
    setEditForm({
      fullname: teacher.fullname || teacher.fullName || '',
      username: teacher.username || '',
      phone: teacher.phone || '',
      personalEmail: teacher.personalEmail || '',
      temporaryPassword: ''
    });
    setEditError('');
    setEditSuccess('');
  }

  async function submitTeacherUpdate(event) {
    event.preventDefault();
    if (!editingTeacherId) return;

    setEditSubmitting(true);
    setEditError('');
    setEditSuccess('');

    try {
      const updateTeacherAccount = httpsCallable(functions, 'updateTeacherAccount');
      await updateTeacherAccount({
        teacherId: editingTeacherId,
        fullname: editForm.fullname,
        username: editForm.username,
        phone: editForm.phone,
        personalEmail: editForm.personalEmail,
        temporaryPassword: editForm.temporaryPassword
      });

      setEditSuccess('Les informations de l’enseignant ont été mises à jour.');
      await loadData();
    } catch (error) {
      console.error('Modification enseignant :', error);
      if (error.code === 'functions/already-exists') {
        setEditError('Cet identifiant est déjà utilisé.');
      } else if (error.code === 'functions/permission-denied') {
        setEditError('Cette action est réservée à la gérance.');
      } else {
        setEditError(error.message || 'Impossible de modifier cet enseignant.');
      }
    } finally {
      setEditSubmitting(false);
    }
  }

  async function submitAssignment(event) {
    event.preventDefault();
    setAssignmentSubmitting(true);
    setAssignmentError('');
    setAssignmentSuccess('');

    try {
      const assignTeacher = httpsCallable(functions, 'assignTeacher');
      const result = await assignTeacher(assignment);
      setAssignmentSuccess(
        `${result.data.teacherName} est maintenant affecté(e) à ${result.data.className} — ${result.data.subjectName}.`
      );
      setAssignment(EMPTY_ASSIGNMENT);
      await loadData();
    } catch (error) {
      console.error('Affectation enseignant :', error);
      setAssignmentError(error.message || 'Impossible d’enregistrer cette affectation.');
    } finally {
      setAssignmentSubmitting(false);
    }
  }

  async function submitTimetable(event) {
    event.preventDefault();
    setTimetableSubmitting(true);
    setTimetableError('');
    setTimetableSuccess('');

    try {
      const createTimetableSession = httpsCallable(functions, 'createTimetableSession');
      const result = await createTimetableSession(timetable);
      setTimetableSuccess(
        `Séance ajoutée : ${result.data.teacherName} — ${result.data.day} ${result.data.startTime}–${result.data.endTime}.`
      );
      setTimetable((current) => ({ ...current, startTime: '', endTime: '', room: '' }));
    } catch (error) {
      console.error('Emploi du temps :', error);
      setTimetableError(error.message || 'Impossible d’ajouter cette séance.');
    } finally {
      setTimetableSubmitting(false);
    }
  }

  if (loading) {
    return <section className="panel personnel-loading">Chargement des enseignants…</section>;
  }

  if (mode === 'add') {
    return (
      <section className="panel personnel-form-panel">
        <div className="personnel-section-heading">
          <span className="personnel-section-icon"><Plus size={22} /></span>
          <div>
            <p className="eyebrow">ÉQUIPE PÉDAGOGIQUE</p>
            <h2>Ajouter un enseignant</h2>
            <p className="muted">Créez son accès et renseignez ses coordonnées principales.</p>
          </div>
        </div>

        {dataError && <div className="alert alert-error">{dataError}</div>}

        <form className="form-stack personnel-form" onSubmit={submitTeacher}>
          <label>Nom et prénom<input name="fullname" value={teacherForm.fullname} onChange={updateTeacherField} required placeholder="Ex. Fatma Ben Salah" /></label>
          <label>Identifiant de connexion<input name="username" value={teacherForm.username} onChange={updateTeacherField} required placeholder="Ex. fatma.bensalah" /></label>
          <label>Téléphone<input name="phone" value={teacherForm.phone} onChange={updateTeacherField} placeholder="Ex. 22 000 000" /></label>
          <label>Email personnel<input type="email" name="personalEmail" value={teacherForm.personalEmail} onChange={updateTeacherField} placeholder="Facultatif" /></label>
          <label>Mot de passe temporaire<input type="text" name="temporaryPassword" value={teacherForm.temporaryPassword} onChange={updateTeacherField} required minLength={8} placeholder="Minimum 8 caractères" /></label>

          <div className="personnel-access-preview">
            <UserRound size={18} />
            <div><strong>Identifiant de connexion</strong><span>{teacherForm.username ? teacherForm.username.trim().toLowerCase() : 'À définir'}</span></div>
          </div>

          {teacherError && <div className="alert alert-error">{teacherError}</div>}
          {teacherSuccess && <div className="success-note"><CheckCircle2 size={18} />{teacherSuccess}</div>}

          <button type="submit" className="button button-primary" disabled={teacherSubmitting}>
            {teacherSubmitting ? 'Création…' : 'Créer le compte enseignant'}
          </button>
        </form>
      </section>
    );
  }

  if (mode === 'assignments') {
    return (
      <section className="panel personnel-form-panel">
        <div className="personnel-section-heading">
          <span className="personnel-section-icon"><GraduationCap size={22} /></span>
          <div>
            <p className="eyebrow">ORGANISATION PÉDAGOGIQUE</p>
            <h2>Affectation pédagogique</h2>
            <p className="muted">Associez un enseignant à une classe et une matière.</p>
          </div>
        </div>

        {dataError && <div className="alert alert-error">{dataError}</div>}
        <form className="form-stack personnel-form" onSubmit={submitAssignment}>
          <label>Enseignant<select value={assignment.teacherId} onChange={(event) => setAssignment((current) => ({ ...current, teacherId: event.target.value }))} required><option value="">Sélectionner un enseignant</option>{teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacherName(teacher)}</option>)}</select></label>
          <label>Classe<select value={assignment.classId} onChange={(event) => setAssignment((current) => ({ ...current, classId: event.target.value, subjectId: '' }))} required><option value="">Sélectionner une classe</option>{classes.map((item) => <option key={item.id} value={item.id}>{schoolClassName(item)}</option>)}</select></label>
          <label>Matière<select value={assignment.subjectId} onChange={(event) => setAssignment((current) => ({ ...current, subjectId: event.target.value }))} required><option value="">Sélectionner une matière</option>{assignmentSubjects.map((item) => <option key={item.id} value={item.id}>{subjectName(item)}</option>)}</select></label>

          {assignmentError && <div className="alert alert-error">{assignmentError}</div>}
          {assignmentSuccess && <div className="success-note"><CheckCircle2 size={18} />{assignmentSuccess}</div>}
          <button type="submit" className="button button-primary" disabled={assignmentSubmitting}>{assignmentSubmitting ? 'Enregistrement…' : 'Enregistrer l’affectation'}</button>
        </form>
      </section>
    );
  }

  if (mode === 'timetable') {
    return (
      <section className="panel personnel-form-panel">
        <div className="personnel-section-heading">
          <span className="personnel-section-icon"><CalendarDays size={22} /></span>
          <div>
            <p className="eyebrow">ORGANISATION PÉDAGOGIQUE</p>
            <h2>Emploi du temps</h2>
            <p className="muted">Ajoutez une séance à partir des affectations déjà enregistrées.</p>
          </div>
        </div>

        {dataError && <div className="alert alert-error">{dataError}</div>}
        <form className="form-stack personnel-form" onSubmit={submitTimetable}>
          <label>Enseignant<select value={timetable.teacherId} onChange={(event) => setTimetable((current) => ({ ...current, teacherId: event.target.value, classId: '', subjectId: '' }))} required><option value="">Sélectionner un enseignant</option>{teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacherName(teacher)}</option>)}</select></label>
          <label>Classe<select value={timetable.classId} onChange={(event) => setTimetable((current) => ({ ...current, classId: event.target.value, subjectId: '' }))} disabled={!timetable.teacherId} required><option value="">Sélectionner une classe</option>{timetableClasses.map((item) => <option key={item.id} value={item.id}>{schoolClassName(item)}</option>)}</select></label>
          <label>Matière<select value={timetable.subjectId} onChange={(event) => setTimetable((current) => ({ ...current, subjectId: event.target.value }))} disabled={!timetable.classId} required><option value="">Sélectionner une matière</option>{timetableSubjects.map((item) => <option key={item.id} value={item.id}>{subjectName(item)}</option>)}</select></label>
          <label>Jour<select value={timetable.day} onChange={(event) => setTimetable((current) => ({ ...current, day: event.target.value }))} required>{['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'].map((day) => <option key={day} value={day}>{day}</option>)}</select></label>
          <div className="personnel-form-columns">
            <label>Heure de début<input type="time" value={timetable.startTime} onChange={(event) => setTimetable((current) => ({ ...current, startTime: event.target.value }))} required /></label>
            <label>Heure de fin<input type="time" value={timetable.endTime} onChange={(event) => setTimetable((current) => ({ ...current, endTime: event.target.value }))} required /></label>
          </div>
          <label>Salle<input value={timetable.room} onChange={(event) => setTimetable((current) => ({ ...current, room: event.target.value }))} placeholder="Facultatif" /></label>

          {timetable.teacherId && timetableClasses.length === 0 && <div className="personnel-inline-note">Cet enseignant n’a encore aucune affectation pédagogique.</div>}
          {timetableError && <div className="alert alert-error">{timetableError}</div>}
          {timetableSuccess && <div className="success-note"><CheckCircle2 size={18} />{timetableSuccess}</div>}
          <button type="submit" className="button button-primary" disabled={timetableSubmitting}>{timetableSubmitting ? 'Enregistrement…' : 'Ajouter la séance'}</button>
        </form>
      </section>
    );
  }

  function displayValue(value, fallback = 'Non renseigné') {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
  }

  function displayDate(value) {
    if (!value) return 'Non renseignée';
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
  }

  return (
    <section className="personnel-teacher-list">
      {dataError && <div className="alert alert-error">{dataError}</div>}
      <div className="panel personnel-list-panel">
        <div className="personnel-section-heading personnel-list-heading">
          <span className="personnel-section-icon"><Users size={22} /></span>
          <div>
            <p className="eyebrow">ÉQUIPE PÉDAGOGIQUE</p>
            <h2>Équipe pédagogique</h2>
            <p className="muted">{teachers.length} enseignant{teachers.length > 1 ? 's' : ''} enregistré{teachers.length > 1 ? 's' : ''}.</p>
          </div>
        </div>

        {!teachersWithAssignments.length ? (
          <div className="compact-empty">Aucun enseignant enregistré.</div>
        ) : (
          <div className="personnel-directory">
            {teachersWithAssignments.map((teacher) => (
              <article key={teacher.id} className={`personnel-person-card ${editingTeacherId === teacher.id ? 'is-editing' : ''}`}>
                <div className="personnel-person-main">
                  <div className="personnel-avatar">{teacherName(teacher).charAt(0).toUpperCase()}</div>
                  <div className="personnel-person-copy">
                    <strong>{teacherName(teacher)}</strong>
                    <span>{teacher.username || teacher.email || 'Enseignant'}</span>
                    <small>{teacher.phone || 'Téléphone non renseigné'}</small>
                  </div>
                  <span className="personnel-status">Actif</span>
                  <div className="teacher-card-actions-v21">
                    <button
                      type="button"
                      className="button button-secondary teacher-profile-open-button-v1"
                      onClick={() => navigate(`/personnel/equipe-pedagogique/${teacher.id}`)}
                    >
                      <Eye size={15} />
                      Voir le dossier
                    </button>
                    <button type="button" className="button button-secondary personnel-edit-button" onClick={() => startEditing(teacher)}><Pencil size={14} />Modifier</button>
                  </div>
                </div>

                <div className="personnel-assignment-tags">
                  {teacher.pedagogicalAssignments.length ? teacher.pedagogicalAssignments.map((item) => (
                    <span key={item.id}><strong>{item.className}</strong> · {item.subjectName}</span>
                  )) : <span className="muted">Aucune affectation pédagogique.</span>}
                </div>


                {editingTeacherId === teacher.id && (
                  <form className="personnel-edit-form" onSubmit={submitTeacherUpdate}>
                    <div className="personnel-form-columns">
                      <label>Nom et prénom<input value={editForm.fullname} onChange={(event) => setEditForm((current) => ({ ...current, fullname: event.target.value }))} required /></label>
                      <label>Identifiant<input value={editForm.username} onChange={(event) => setEditForm((current) => ({ ...current, username: event.target.value }))} required /></label>
                      <label>Téléphone<input value={editForm.phone} onChange={(event) => setEditForm((current) => ({ ...current, phone: event.target.value }))} /></label>
                      <label>Email personnel<input type="email" value={editForm.personalEmail} onChange={(event) => setEditForm((current) => ({ ...current, personalEmail: event.target.value }))} /></label>
                    </div>
                    <label>Nouveau mot de passe temporaire <span className="muted">(facultatif)</span><input type="text" minLength={8} value={editForm.temporaryPassword} onChange={(event) => setEditForm((current) => ({ ...current, temporaryPassword: event.target.value }))} placeholder="Laisser vide pour ne pas le changer" /></label>
                    {editError && <div className="alert alert-error">{editError}</div>}
                    {editSuccess && <div className="success-note"><CheckCircle2 size={18} />{editSuccess}</div>}
                    <div className="personnel-edit-actions">
                      <button type="button" className="button button-secondary" onClick={() => setEditingTeacherId('')}>Annuler</button>
                      <button type="submit" className="button button-primary" disabled={editSubmitting}>{editSubmitting ? 'Enregistrement…' : 'Enregistrer les modifications'}</button>
                    </div>
                  </form>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
